import { GRAVITY, REST_SPEED } from '../constants.js';
import { isSolid, lineHit, surfaceNormal } from '../terrain/mask.js';

export interface Body {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface MotionParams {
  /** Bounce energy kept along the normal (0 = dead, 0.7 = lively coconut). */
  restitution: number;
  /** Tangential velocity kept per impact (0.97 = rolls forever, 0.5 = sticky). */
  friction: number;
  gravityScale: number;
  windScale: number;
}

export interface Impact {
  x: number;
  y: number;
  nx: number;
  ny: number;
  /** Speed just before impact — bounce thresholds read this. */
  speed: number;
}

export const DEFAULT_MOTION: MotionParams = {
  restitution: 0.35,
  friction: 0.88,
  gravityScale: 1,
  windScale: 1,
};

/**
 * Integrate a projectile for `dt` with internal substeps + swept collision
 * (≈1px sampling), slope-aware reflection, tangential friction and rolling.
 * Returns the first impact this step, if any.
 */
export function stepProjectile(
  p: Body,
  params: MotionParams,
  terrain: Uint8Array,
  wind: number,
  dt: number,
  substeps = 4,
): Impact | null {
  const sdt = dt / substeps;
  for (let s = 0; s < substeps; s++) {
    p.vx += wind * params.windScale * sdt;
    p.vy += GRAVITY * params.gravityScale * sdt;

    const x1 = p.x + p.vx * sdt;
    const y1 = p.y + p.vy * sdt;
    const hit = lineHit(terrain, p.x, p.y, x1, y1);

    if (hit) {
      const speed = Math.hypot(p.vx, p.vy);
      const n = surfaceNormal(terrain, hit.x, hit.y);
      // Push just outside the surface so gravity doesn't re-embed us.
      p.x = hit.x + n.x * 1.5;
      p.y = hit.y + n.y * 1.5;

      const vn = p.vx * n.x + p.vy * n.y;
      if (vn < 0) {
        const j = -(1 + params.restitution) * vn;
        p.vx += j * n.x;
        p.vy += j * n.y;
        // Tangential friction.
        const tx = -n.y;
        const ty = n.x;
        const vt = p.vx * tx + p.vy * ty;
        const kept = vt * params.friction;
        const vn2 = p.vx * n.x + p.vy * n.y;
        p.vx = n.x * vn2 + tx * kept;
        p.vy = n.y * vn2 + ty * kept;
      }
      return { x: hit.x, y: hit.y, nx: n.x, ny: n.y, speed };
    }
    p.x = x1;
    p.y = y1;
  }

  // Safety: never allow an embedded projectile to sit inside terrain.
  if (isSolid(terrain, p.x, p.y)) {
    for (let i = 0; i < 10 && isSolid(terrain, p.x, p.y); i++) p.y -= 2;
  }
  return null;
}

/** Speed below which a projectile is considered resting. */
export function isResting(p: Body): boolean {
  return Math.hypot(p.vx, p.vy) < REST_SPEED;
}

/**
 * Integrate under gravity/wind with swept collision — used by the aim solver
 * and trajectory preview. Returns sampled points plus the landing state.
 */
export function simulateTrajectory(
  x0: number,
  y0: number,
  vx0: number,
  vy0: number,
  terrain: Uint8Array,
  wind: number,
  opts: {
    gravityScale?: number;
    windScale?: number;
    maxT?: number;
    step?: number;
    /** Stop sampling once this many points are collected (preview budget). */
    maxPoints?: number;
  } = {},
): { points: { x: number; y: number }[]; endX: number; endY: number; hitTerrain: boolean; t: number } {
  const gravityScale = opts.gravityScale ?? 1;
  const windScale = opts.windScale ?? 1;
  const maxT = opts.maxT ?? 8;
  const step = opts.step ?? 1 / 60;
  const maxPoints = opts.maxPoints ?? 400;

  const p: Body = { x: x0, y: y0, vx: vx0, vy: vy0 };
  const params: MotionParams = {
    restitution: 0,
    friction: 1,
    gravityScale,
    windScale,
  };
  const points = [{ x: p.x, y: p.y }];
  let t = 0;
  while (t < maxT) {
    const impact = stepProjectile(p, params, terrain, wind, step, 4);
    t += step;
    if (impact) {
      points.push({ x: impact.x, y: impact.y });
      return { points, endX: impact.x, endY: impact.y, hitTerrain: true, t };
    }
    if (points.length < maxPoints) points.push({ x: p.x, y: p.y });
    // Left the world → treat as end (falling out).
    if (p.y > 20000 || p.x < -4000 || p.x > 40000) {
      return { points, endX: p.x, endY: p.y, hitTerrain: false, t };
    }
  }
  return { points, endX: p.x, endY: p.y, hitTerrain: false, t };
}
